/**
 * @file helpers.js
 * @description General-purpose helper functions
 */

/**
 * Reorder items after a drag-and-drop event (react-beautiful-dnd)
 * @param {object} result
 * @param {object} columns
 * @param {Function} setColumns
 */
export function onDragEnd(result, columns, setColumns) {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId !== destination.droppableId) {
        const srcCol = columns[source.droppableId];
        const dstCol = columns[destination.droppableId];
        const srcItems = [...srcCol.items];
        const dstItems = [...dstCol.items];
        const [removed] = srcItems.splice(source.index, 1);
        dstItems.splice(destination.index, 0, removed);
        setColumns({
            ...columns,
            [source.droppableId]: { ...srcCol, items: srcItems },
            [destination.droppableId]: { ...dstCol, items: dstItems },
        });
    } else {
        const col = columns[source.droppableId];
        const items = [...col.items];
        const [removed] = items.splice(source.index, 1);
        items.splice(destination.index, 0, removed);
        setColumns({ ...columns, [source.droppableId]: { ...col, items } });
    }
}

/**
 * Convert an array into a comma-separated string
 * @param {Array} arr
 * @returns {string}
 */
export function explodeArray(arr) {
    if (!Array.isArray(arr)) return arr;
    if (arr.length === 0) return "";
    const head = arr.slice(0, -1).join(", ");
    return head + (arr.length > 1 ? `, ${arr[arr.length - 1]}` : "");
}

/**
 * Strip file extension from name
 * @param {string} name
 * @returns {string}
 */
export function trimFileNameExt(name) {
    return name?.split(".")[0];
}

/**
 * Generate a random filename with the given extension
 * @param {number} length
 * @param {string} fileType
 * @param {string[]} allowedTypes
 * @returns {string|null}
 */
export function generateFileName(length, fileType, allowedTypes = []) {
    const ext = fileType.toLowerCase() === "mpeg" ? "mp3" : fileType.toLowerCase();
    if (allowedTypes.length && !allowedTypes.includes(ext)) return null;
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const rand = Array.from({ length }, () =>
        chars.charAt(Math.floor(Math.random() * chars.length))
    ).join("");
    return `${rand}.${ext}`;
}

/**
 * Normalise RSS settings from different sources
 * @param {object} setting
 * @param {string} [title]
 * @returns {object}
 */
export function normalizeRssSettings(setting, title = "") {
    return {
        ...setting,
        name: title.split(".")[0],
        title: title.split(".")[0],
        text_color: setting?.text_color ?? setting?.textColor,
        direction: setting?.direction === "ltr" ? "left" : "right",
        font_size: Number(setting?.font_size),
        bg_color: setting?.bg_color ?? setting?.bgColor,
    };
}

/**
 * Validate that value is not "default" (reserved name)
 * @param {string} value
 * @returns {true|string}
 */
export function validateNotReserved(value) {
    return (
        value.toLowerCase() !== "default" || `"default" is a reserved name!`
    );
}

/**
 * Resolve a device's REACHABILITY (green dot vs red/purple dot) — not
 * whether it's actively playing.
 *
 * Unified-presence rollout, part 2 (2026-10-06): 100% backend-computed now,
 * zero RTDB. `connectivity.state` ('offline'|'online'|'standby'|
 * 'app_closed') is $appends'd on every device row — list AND detail — so
 * standby/app_closed are available everywhere without a live RTDB listener.
 * 'standby' and 'app_closed' count as reachable (true), same as 'online' —
 * only 'offline' is false. Consumers that need "is this device ACTIVELY
 * PLAYING right now" must check `resolveDeviceStatus(...) === 'online'`
 * instead — this boolean is not that signal.
 *
 * `apiOnlineFallback` (the old plain `online` boolean) is consulted ONLY if
 * `connectivity` itself is somehow absent (a cached response from before
 * this rollout) — in normal operation `connectivity` is always present.
 *
 * @param {Object} [connectivity] device.connectivity, backend-computed
 * @param {boolean} [apiOnlineFallback]
 * @returns {boolean}
 */
export function resolveDeviceOnline(connectivity, apiOnlineFallback) {
    if (connectivity?.state === undefined) return apiOnlineFallback === true;
    return connectivity.state !== 'offline';
}

/**
 * Same as resolveDeviceOnline but exposes the full status for surfaces that
 * render a status badge/label — 'standby'/'app_closed' shown distinctly
 * instead of collapsing to "Online"/"Offline".
 *
 * `presenceStatus` (device.presence_status, unchanged/additive since before
 * this rollout) still layers 'content_corrupted' (reachable, app heartbeat
 * fresh, but the player's own full-cycle content give-up fired) and
 * 'scheduled_off' (unreachable, but currently inside the device's own
 * configured Working-Hours off-window) on top — these are NOT part of
 * `connectivity.state`'s 4-value enum by design (they're content/schedule
 * facts, not RTDB-derived presence), so they're checked first and otherwise
 * `connectivity.state` is returned exactly as given.
 *
 * @param {Object} [connectivity] device.connectivity, backend-computed
 * @param {string} [presenceStatus] device.presence_status
 * @param {boolean} [apiOnlineFallback] only used if connectivity is absent
 * @returns {'online'|'offline'|'scheduled_off'|'content_corrupted'|'standby'|'app_closed'}
 */
export function resolveDeviceStatus(connectivity, presenceStatus, apiOnlineFallback) {
    if (presenceStatus === 'content_corrupted' || presenceStatus === 'scheduled_off') return presenceStatus;
    if (connectivity?.state !== undefined) return connectivity.state;
    return apiOnlineFallback === true ? 'online' : 'offline';
}

/**
 * build 396 (Part D) -- EVERY screen state the product knows, in one list, shared by the Screens card and the one-screen page.
 * 'paused' is the Screens card's pause chip promoted to a first-class state: the device is ONLINE and its backend-reported playback
 * is 'paused' (connectivity.playback, i.e. the heartbeat's is_playing -- the SAME source the card's chip has always used).
 * Mirrors the backend's Device::SCREEN_STATES; screenStateContract.json (this repo AND the backend) pins the two together.
 */
export const SCREEN_STATES = ['online', 'offline', 'scheduled_off', 'content_corrupted', 'standby', 'app_closed', 'paused'];

/**
 * resolveDeviceStatus() + the pause chip, as ONE value. `playbackOverride` lets the card pass its optimistic play/pause guess; without
 * it the server's connectivity.playback decides (what the backend's Device::screenState() uses).
 */
export function resolveScreenState(connectivity, presenceStatus, apiOnlineFallback, playbackOverride, healthState) {
    const status = resolveDeviceStatus(connectivity, presenceStatus, apiOnlineFallback);
    if (status !== 'online') return status;
    const playing = playbackOverride !== undefined ? playbackOverride : resolvePlayback(connectivity);
    if (playing !== false) return 'online';
    // build 402: a screen that is playing WITHOUT SOUND, or still LOADING, is not paused (the old rule called every is_playing=false a pause --
    // "paused" for 7 hours while the video was playing). It keeps its 'online' state; the chip/label comes from resolvePlaybackDetail().
    // An optimistic override of false (the user just pressed pause) is still a pause.
    if (playbackOverride === undefined) {
        const detail = resolvePlaybackDetail(connectivity);
        // build 404: an item that froze and is being skipped, and the prayer pause, are not "paused" either
        if (detail === 'no_sound' || detail === 'loading' || detail === 'media_stalled' || detail === 'prayer' || detail === 'external_fault') return 'online';
    }
    // A screen whose playback WATCHDOG reported it stalled also reports is_playing=false. The backend decided that is a FAULT, not a
    // pause (customer state needs_you, not paused): when the health entry is loaded and does not say 'paused', it is not a pause.
    if (healthState !== undefined && healthState !== null && healthState !== 'paused') return 'online';
    return 'paused';
}

/**
 * build 402 -- the ONE playback label rule, shared with the backend (Device::playbackDetail) and the remote:
 *   'no_sound' (is_playing=false + an audio_* fault)  -> "Playing without sound"
 *   'loading'  (is_playing=false + buffering)         -> "Loading"
 *   'media_stalled' (is_playing=false + reason media_stalled: build 404 -- an item froze, the player is skipping it) -> "Item stalled - moving to the next"
 *   'external_fault' (is_playing=false + reason audio_external_fault: build 405 -- the connected USB sound device is faulty, automatic repairs suspended)
 *   'prayer'        (is_playing=false + reason prayer)                                                           -> "Prayer time"
 *   'paused' | 'playing' | 'unknown'
 * The server computes it (connectivity.playback_detail). An OLD backend / payload without it falls back to connectivity.playback, i.e. exactly
 * what this app did before: playing -> 'playing', paused -> 'paused', anything else -> 'unknown'.
 */
export function resolvePlaybackDetail(connectivity) {
    const d = connectivity?.playback_detail;
    if (d === 'no_sound' || d === 'loading' || d === 'media_stalled' || d === 'prayer' || d === 'external_fault' || d === 'paused' || d === 'playing' || d === 'unknown') return d;
    if (connectivity?.playback === 'playing') return 'playing';
    if (connectivity?.playback === 'paused') return 'paused';
    return 'unknown';
}

/**
 * Build 383, Part 3 — single-source-of-truth play state. The play/pause
 * button used to read RTDB `DeviceState/isPlaying` directly
 * (`realtimeData.is_playing` in DeviceCard.jsx); that's now a
 * compatibility mirror for old remotes only (see popuppush-app-v2's
 * CLAUDE.md) — this reads the backend's `connectivity.playback` field
 * instead, same `GET /groups/company` 15s poll every other connectivity
 * field already uses.
 *
 * `connectivity.playback` is one of 'playing' | 'paused' | 'unknown'
 * ('unknown' while offline, or for a device that has never heartbeated
 * `is_playing` at all — an older player build). Maps to true/false/null —
 * null meaning "don't know yet", the same tri-state the button's own
 * loading-disabled state already used.
 *
 * @param {Object} [connectivity] device.connectivity, backend-computed
 * @returns {boolean|null}
 */
export function resolvePlayback(connectivity) {
    // build 411: a screen whose app is closed / that is on standby / offline is not "playing", whatever a stale payload (an older backend, a cached row) still says
    if (connectivity?.state !== undefined && connectivity.state !== 'online') return null;
    if (connectivity?.playback === 'playing') return true;
    if (connectivity?.playback === 'paused') return false;
    return null;
}

/**
 * After a play/pause press, the button shows the PREDICTED new state
 * immediately (optimistic) instead of waiting for the next poll — but
 * keeps showing it even if that poll's server value hasn't caught up yet
 * (the player's own heartbeat can lag a press by up to ~10s), until
 * EITHER the server confirms it OR `OPTIMISTIC_PLAYBACK_MAX_WAIT_MS`
 * (20s) has elapsed, whichever comes first — after that it always
 * follows the server, even if the server never confirms (e.g. the press
 * silently failed to reach the device), never stuck showing a guess
 * forever. Pure decision, `now` injected so it's testable without a real
 * clock — mirrors popuppush-remote's `resolveOptimisticPlayback` (same
 * problem, same fix, different codebase).
 *
 * @param {Object} params
 * @param {boolean|null} params.serverIsPlaying
 * @param {boolean|null} params.optimisticIsPlaying
 * @param {number|null} params.optimisticSetAt epoch ms
 * @param {number} params.now epoch ms
 * @returns {{displayIsPlaying: boolean|null, stillOptimistic: boolean}}
 */
export const OPTIMISTIC_PLAYBACK_MAX_WAIT_MS = 20000;

export function resolveOptimisticPlayback({ serverIsPlaying, optimisticIsPlaying, optimisticSetAt, now }) {
    if (optimisticIsPlaying === null || optimisticIsPlaying === undefined || optimisticSetAt == null) {
        return { displayIsPlaying: serverIsPlaying, stillOptimistic: false };
    }
    const confirmed = serverIsPlaying === optimisticIsPlaying;
    const timedOut = (now - optimisticSetAt) > OPTIMISTIC_PLAYBACK_MAX_WAIT_MS;
    if (confirmed || timedOut) {
        return { displayIsPlaying: serverIsPlaying, stillOptimistic: false };
    }
    return { displayIsPlaying: optimisticIsPlaying, stillOptimistic: true };
}


/**
 * The tag the wizards put on the playlists they make is stored as "Auto-created" (a stable key in the database). Shown to people it uses the
 * translated label «تلقائي»; every other tag shows its own name.
 */
export const AUTO_TAG_NAME = "Auto-created";
export const tagDisplayName = (tag, t) => (tag?.name === AUTO_TAG_NAME ? t("playlist.auto_tag_label") : tag?.name);
